import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
// import * as Sentry from "@sentry/nextjs";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "main/features/AxiosWrapper";
import { ErrorResponse } from "main/types/errorResponse";
import { User } from "main/types/user";
import {
  deepMerge,
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "main/utils/sliceUtil";
import { API_BASE_PATH, LOCATION_BASE_PATH } from "main/constants/constant";
import { GetServerSidePropsContext } from "next";
import { getBrowserUrl } from "main/utils/hostUtil";
import { parseDate } from "main/utils/dateUtil";
import { getDate, getMonth, getYear, isValid } from "date-fns";
import { RootState } from "main/store";

const key = "user";
const adapter = createEntityAdapter<User.User>();

const initialStateValues: User.UserState = {
  user: null,
  chatUnit: null,
  errors: null,
  loading: false,
  isChecked: false,
  snsAuthentication: null,
  isLiffInitialized: false,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async * */
export const authUser = createAsyncThunk<
  {
    user: User.User;
  },
  {
    companyCode: string;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/authUser`, async (args, { rejectWithValue }) => {
  try {
    const { companyCode, headers, isServerSide = false, locale } = args;
    // console.log(headers);
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/v1/${companyCode}/current_user.json`,
      { headers },
    );
    // console.log(result);
    return {
      user: result.data.user,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const authChatUnit = createAsyncThunk<
  {
    chatUnit: ChatUnit.ChatUnit;
  },
  {
    hotelId: string;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/authChatUnit`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, headers, isServerSide = false, locale } = args;
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/v1/${hotelId}/current_chat_unit.json`,
      { headers },
    );
    return {
      chatUnit: result.data.chatUnit,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

interface SignInParams {
  email: string;
  password: string;
}

export const signInUser = createAsyncThunk<
  {
    user: User.User;
  },
  { hotelId: string; params: SignInParams },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/signInUser`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, params } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/${hotelId}/user_auth/sign_in`,
      {
        ...params,
      },
    );
    // console.log(result);
    return {
      user: result.data.user,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const signOutUser = createAsyncThunk<
  void,
  { hotelId: string },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/signOutUser`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId } = args;
    await createWrapper().delete(
      `${API_BASE_PATH}/${hotelId}/user_auth/sign_out`,
    );
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const signUpUser = createAsyncThunk<
  {
    user: User.User;
  },
  { hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/signUpUser`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, params } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/${hotelId}/user_auth.json`,
      params,
    );
    return {
      user: result.data.user,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postPasswordReset = createAsyncThunk<
  void,
  { companyCode: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postPasswordReset`, async (args, { rejectWithValue }) => {
  try {
    const { companyCode, params } = args;
    await createWrapper().post(
      `${API_BASE_PATH}/${companyCode}/user_auth/password_reset.json`,
      { user: params },
    );
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putPasswordReset = createAsyncThunk<
  void,
  { companyCode: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putPasswordReset`, async (args, { rejectWithValue }) => {
  try {
    const { companyCode, params } = args;
    await createWrapper().put(
      `${API_BASE_PATH}/${companyCode}/user_auth/password_reset.json`,
      { user: params },
    );
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const getSnsAuthentication = createAsyncThunk<
  {
    snsAuthentication: User.SnsAuthentication;
  },
  {
    id: string;
    hotelId: string;
    token: string;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/getSnsAuthentication`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, token, headers, isServerSide = false, locale } = args;
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/sns_authentications/${id}.json?token=${token}`,
      { headers },
    );
    return { snsAuthentication: result.data.tmpSnsAuthentication };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putSnsAuthentication = createAsyncThunk<
  void,
  { id: string; hotelId: string; token: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putSnsAuthentication`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, token, params } = args;
    await createWrapper().put(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/sns_authentications/${id}.json`,
      {
        token: token,
        sns_authentication: params,
      },
    );
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const lineAutoSignIn = createAsyncThunk<
  {
    chatUnit: ChatUnit.ChatUnit;
  },
  { hotelId: string; accessToken: string },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/lineAutoSignIn`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, accessToken } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/${hotelId}/chat_unit_auth/line/sign_in.json`,
      {
        access_token: accessToken,
      },
    );
    return {
      chatUnit: result.data.chatUnit,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putCurrentUser = createAsyncThunk<
  {
    user: User.User;
  },
  { companyCode: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putCurrentUser`, async (args, { rejectWithValue }) => {
  try {
    const { companyCode, params } = args;
    const result = await createWrapper().put(
      `${API_BASE_PATH}/v1/${companyCode}/current_user.json`,
      {
        user: params,
      },
    );
    return {
      user: result.data.user,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const deleteCurrentUser = createAsyncThunk<
  void,
  { companyCode: string },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/deleteCurrentUser`, async (args, { rejectWithValue }) => {
  try {
    const { companyCode } = args;
    await createWrapper().delete(
      `${API_BASE_PATH}/v1/${companyCode}/current_user.json`,
    );
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const requestUserEmailVerification = createAsyncThunk<
  void,
  { id: number; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/requestUserEmailVerification`, async (args, { rejectWithValue }) => {
  try {
    const { id, params } = args;
    await createWrapper().post(
      `${API_BASE_PATH}/v1/users/${id}/email_verification_mails.json`,
      params,
    );
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postUserEmailVerification = createAsyncThunk<
  void,
  { id: number; params?: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postUserEmailVerification`, async (args, { rejectWithValue }) => {
  try {
    const { id, params } = args;
    await createWrapper().post(
      `${API_BASE_PATH}/v1/users/${id}/email_verifications.json`,
      params,
    );
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice * */
export const userSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setCurrentUser: (state, action) => {
      state.user = action.payload;
    },
    setIsLiffInitialized: (state, action) => {
      state.isLiffInitialized = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        // if (action.payload.user?.user) {
        //   Sentry.configureScope((scope) => {
        //     scope.setUser({
        //       id: `${action.payload.user.user.modelNameAndId}`,
        //     });
        //   });
        // }
        const user = deepMerge(state, action.payload.user);
        return { ...state, ...user };
      })
      .addCase(authUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isChecked = true;
      })
      .addCase(authUser.rejected, (state) => {
        state.isChecked = true;
      })
      .addCase(authChatUnit.fulfilled, (state, action) => {
        state.chatUnit = action.payload.chatUnit;
        state.isChecked = true;
      })
      .addCase(authChatUnit.rejected, (state) => {
        state.isChecked = true;
      })
      .addCase(signInUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
      })
      .addCase(signOutUser.fulfilled, (state, _) => {
        state.user = null;
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
      })
      .addCase(getSnsAuthentication.fulfilled, (state, action) => {
        state.snsAuthentication = action.payload.snsAuthentication;
      })
      .addCase(lineAutoSignIn.fulfilled, (state, action) => {
        state.chatUnit = action.payload.chatUnit;
      })
      .addCase(putCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.errors = null;
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector * */
const stateSelector = (state: RootState) => state[key];

export const currentUserSelector = createSelector(
  stateSelector,
  (state) => state.user,
);

export const errorSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const isCheckedSelector = createSelector(
  stateSelector,
  (state) => state.isChecked,
);

export const snsAuthenticationSelector = createSelector(
  stateSelector,
  (state) => state.snsAuthentication,
);

// action export
export const { setCurrentUser, setIsLiffInitialized } = userSlice.actions;

// for Server side
export const redirectToSignIn = (
  ctx: GetServerSidePropsContext,
  hotelCode: string,
) => {
  ctx.res.writeHead(302, {
    Location: `${getBrowserUrl(
      ctx.locale,
    )}${LOCATION_BASE_PATH}/hotels/${hotelCode}/signin`,
  });
  ctx.res.end();
};

export const convertUserAccountFormValuesToParams = (data: any) => {
  const params = { ...data };
  const profile = params.profile;
  if (
    profile.date_of_birth_year &&
    profile.date_of_birth_month &&
    profile.date_of_birth_day
  ) {
    const month = String(profile.date_of_birth_month).padStart(2, "0");
    const day = String(profile.date_of_birth_day).padStart(2, "0");
    profile.birthday = `${profile.date_of_birth_year}-${month}-${day}`;
  }
  delete profile.date_of_birth_year;
  delete profile.date_of_birth_month;
  delete profile.date_of_birth_day;
  params.profile = profile;
  return params;
};

export const convertUserToFormValues = (data: any) => {
  const values = { ...data };
  const profileValues = values.profile;
  if (profileValues.birthday) {
    const birthdayDate = parseDate(profileValues.birthday);
    if (isValid(birthdayDate)) {
      profileValues.date_of_birth_year = getYear(birthdayDate);
      profileValues.date_of_birth_month = getMonth(birthdayDate) + 1;
      profileValues.date_of_birth_day = getDate(birthdayDate);
    }
    delete profileValues.birthday;
  }
  if (!profileValues.country_code) {
    profileValues.country_code = "JP";
  }
  values.profile = profileValues;
  return values;
};
